import React, { type JSX } from 'react';
import { useTypedSelector, useAppDispatch } from '@fiji/common/src/app/store';
import { selectedOrg } from '@fiji/common/src/features/orgManagement/orgSlice';
import { Avatar, Chip, Paper, Skeleton, Stack, Typography } from '@mui/material';
import { CustomTable, CustomTransComponent, DateAndYearSkelton } from '../../../components';
import { useGetAllDashboardsQuery } from '@fiji/common/src/features/dashboardManagement/dashboardApi';
import Check from '@mui/icons-material/Check';
import ChevronRight from '@mui/icons-material/ChevronRight';
import Dashboard from '@mui/icons-material/Dashboard';
import Delete from '@mui/icons-material/Delete';
import DriveFileRenameOutline from '@mui/icons-material/DriveFileRenameOutline';
import FileCopy from '@mui/icons-material/FileCopy';
import { useTheme } from '@mui/material/styles';
import CustomIcon from '../../../components/CustomIcon';
import { DeleteDashboardModal } from './DashboardDeleteModal';
import { useNavigate } from 'react-router-dom';
import { DateCalendar } from '@mui/x-date-pickers-pro';
import { EditDashboardDetails } from '../Common';
import { useFilteredValues } from '../../../hooks/useFilteredValues';
import { AssociateDeviceModal, CreateDashboardModal } from '../CreateDashboard';
import { toFirstLetterUpperCase } from '../../../utils/helpers';
import { useDebounce, useIsMount } from '@fiji/common/src/hooks';
import { CustomMenu } from 'components/CustomMenu';
import { selectCurrentPermission } from '@fiji/common/src/features/profile/profileSlice';
import { useRBAC } from 'hooks';
import { useTransformData } from 'hooks/useTransformData';
import { InputWithCustomPlaceholder } from 'components/InputWithCustomPlaceholder';
import { useTranslation } from 'react-i18next';
import { resetLocalDashboard } from '@fiji/common/src/features/dashboardManagement/dashboardSlice';

export const DashboardList = (): JSX.Element => {
    const theme: any = useTheme();
    const { t } = useTranslation();
    const currentOrg = useTypedSelector(selectedOrg);
    const permissions = useTypedSelector(selectCurrentPermission);
    const { hasPermission } = useRBAC(permissions);
    const canUpdateSystemDashboard = hasPermission('dashboard-management_edit-system-dashboards');
    const canUpdateUserDashboard = hasPermission('dashboard-management_edit-user-dashboards');
    const canDeleteSystemDashboard = hasPermission('dashboard-management_delete-system-dashboards');
    const canDeleteUserDashboard = hasPermission('dashboard-management_delete-user-dashboards');
    const canCreateSystemDashboard = hasPermission('dashboard-management_create-system-dashboards');
    const canCreateUserDashboard = hasPermission('dashboard-management_create-user-dashboards');
    const canViewUserDashboards = hasPermission('dashboard-management_view-user-dashboards');
    const canViewSystemDashboards = hasPermission('dashboard-management_view-system-dashboards');
    const navigate = useNavigate();
    const [payload, setPayload] = React.useState<any>({
        page: 0,
        size: 10,
        filters: {},
    });
    const [date, setDate] = React.useState<any>(null);
    const [searchKey, setSearchKey] = React.useState<any>(undefined);

    const [, debouncedValue] = useDebounce(undefined, undefined, searchKey);
    const { convertDateTime } = useTransformData();
    const tableRef: any = React.useRef(null);
    const deleteRef: any = React.useRef(null);
    const editRef: any = React.useRef(null);
    const cloneRef: any = React.useRef(null);
    const associateDeviceRef: any = React.useRef(null);
    const isMount = useIsMount();
    const dispatch = useAppDispatch();

    const handleFilterChange = (filters: any, sortData?: any): void => {
        if (JSON.stringify(filters) !== JSON.stringify(payload.filters)) {
            const customKeys: any = {
                ...(payload.filters?.updatedAt && { updatedAt: payload.filters?.updatedAt }),
            };
            setPayload((prev: any) => ({
                ...prev,
                page: 0,
                filters: { ...filters, ...(isMount ? payload.filters : {}), ...customKeys },
            }));
        } else if (sortData && Object.keys(sortData)?.length) {
            setPayload((prev: any) => ({
                ...prev,
                page: 0,
                sort: sortData,
            }));
        }
    };

    React.useEffect(() => {
        if (!isMount) {
            setPayload((prev: any) => ({
                ...prev,
                page: 0,
                searchKey: debouncedValue,
            }));
        }
    }, [debouncedValue]);

    const currentRealmName = useTypedSelector((state) => state.common.selectedRealm);
    const handleDashboardPageChange = (page: any, size: any): void => {
        setPayload((prev: any) => ({ ...prev, page: page, size: size }));
    };

    const canModifySystemDashboard = (data: any): boolean =>
        data?.isSystemDashboard &&
        (canUpdateSystemDashboard || canDeleteSystemDashboard || canCreateSystemDashboard || canCreateUserDashboard);

    const canModifyUserDashboard = (data: any): boolean =>
        !data?.isSystemDashboard &&
        (canUpdateUserDashboard || canDeleteUserDashboard || canCreateUserDashboard || canCreateSystemDashboard);

    const canModifyDashboard = (data: any): boolean => canModifySystemDashboard(data) || canModifyUserDashboard(data);

    const canEditDashboard = (data: any): boolean =>
        (data?.isSystemDashboard && canUpdateSystemDashboard) || (!data?.isSystemDashboard && canUpdateUserDashboard);

    const getActionCell = (data: any): JSX.Element => (
        <Stack direction={'row'} alignItems={'center'} gap={1}>
            {canModifyDashboard(data) && <CustomMenu menuList={getActionsBtn(data)} />}
            {canEditDashboard(data) && (
                <ChevronRight
                    key={data?.id}
                    data-testid="ChevronRightIcon"
                    onClick={(): void => {
                        dispatch(resetLocalDashboard());
                        navigate(`/${currentRealmName}/editDashboard/${data?.assignTypeIds[0]}/${data?.id}`, {
                            state: {
                                assignType: data?.assignType,
                                assignId: data?.assignTypeIds[0],
                                isDefaultRoot: true,
                            },
                        });
                    }}
                    className="cursor-pointer"
                />
            )}
        </Stack>
    );
    const getAssignment = (data: any, key: string): JSX.Element => {
        if (data?.isSystemDashboard) {
            return <Typography variant="body1">System</Typography>;
        }

        const name = data?.[key]?.name;
        const email = data?.[key]?.email;

        // Check if name is valid (not null, not "null null", not empty)
        const isValidName = name && name.trim() && !name.includes('null');

        return (
            <>
                <Typography variant="body1">{isValidName ? name : email ?? '--'}</Typography>
                <Typography variant="body2">{email ?? '--'}</Typography>
            </>
        );
    };

    const getUpdateTime = (data: any): JSX.Element =>
        data['updatedAt'] ? (
            <>
                <Typography variant="body1" fontSize={'14px'} fontWeight={'400'}>
                    {convertDateTime({
                        timestamp: data['updatedAt'],
                    })}
                </Typography>
                <Typography variant="body2" fontSize={'12px'} fontWeight={'400'}>
                    {`${convertDateTime({
                        timestamp: data['updatedAt'],
                        customFormat: 'HH:mm:ss',
                    })}`}
                </Typography>
            </>
        ) : (
            <>--</>
        );

    const getName = (data: any): JSX.Element => (
        <Stack direction={'row'} alignItems={'center'} gap={1}>
            <Avatar sx={{ bgcolor: '#EEF0F0', color: '#727E84' }}>
                <CustomIcon iconName={data['icon']?.web?.name} family={data['icon']?.web?.family} />
            </Avatar>
            <Typography variant="body1" fontSize={'14px'} fontWeight={'600'}>
                {data['name'] ?? '--'}
            </Typography>
        </Stack>
    );

    const getActionsBtn = (data: any): any => {
        const menuGroups = [];

        if (
            (data?.isSystemDashboard && canUpdateSystemDashboard) ||
            (!data?.isSystemDashboard && canUpdateUserDashboard)
        ) {
            menuGroups.push(
                {
                    icon: <DriveFileRenameOutline />,
                    title: <CustomTransComponent translationKey={'COMMON:EDIT_DETAILS'} />,
                    onClick: (): void => editRef?.current?.handleModalAction?.(true, data),
                },
                {
                    icon: <FileCopy />,
                    title: <CustomTransComponent translationKey={'COMMON:ASSOCIATE_DEVICES'} />,
                    className: data?.assignType === 'GROUP' || data?.assignType === 'SOURCE' ? 'disabled' : '',
                    onClick: (): void => associateDeviceRef?.current?.handleModalAction(true, data),
                }
            );
        }

        if (canCreateSystemDashboard || canCreateUserDashboard) {
            menuGroups?.push({
                icon: <FileCopy />,
                title: <CustomTransComponent translationKey={'COMMON:CLONE'} />,
                onClick: (): void => cloneRef?.current?.handleModalAction?.(true, data),
            });
        }
        if (
            (data?.isSystemDashboard && canDeleteSystemDashboard) ||
            (!data?.isSystemDashboard && canDeleteUserDashboard)
        ) {
            menuGroups.push({
                icon: <Delete />,
                title: <CustomTransComponent translationKey={'COMMON:DELETE'} />,
                onClick: (): void => deleteRef?.current?.handleModalAction?.(data),
            });
        }

        return {
            id: 'profile-menu',
            menuGroups,
            menuTitle: '',
            menuSubtitle: '',
        };
    };

    React.useEffect(() => {
        if (date) {
            setPayload((prev: any) => ({
                ...prev,
                page: 0,
                filters: { ...prev.filters, updatedAt: new Date(date)?.getTime() + 86399000 },
            }));
        }
    }, [date]);

    const getDateHeaderOptions = (): JSX.Element => (
        <DateCalendar disableFuture value={date ?? null} onChange={(data: any): void => setDate(data)} />
    );

    const getDescription = (data: any): JSX.Element => (
        <Typography variant="body1">{data?.description ?? ''}</Typography>
    );

    const getDateSx = (): any => ({
        width: 'auto !important',
        maxHeight: '500px !important',
    });

    const getActive = (data: any): JSX.Element =>
        data['assignType'] === 'SOURCE' && data['status'] === 'ACTIVE' ? (
            <Check sx={{ color: theme?.palette?.primary?.main }} />
        ) : (
            <></>
        );

    const handleChipDelete = (key: any): void => {
        tableRef?.current?.resetFilters(key === 'groupId' ? 'groupPath' : key, true);
        const paginationPayloadClone = JSON.parse(JSON.stringify(payload));
        delete paginationPayloadClone?.filters[key];
        if (key === 'updatedAt') setDate(null);
        setPayload(paginationPayloadClone);
    };

    const headers: any = [
        {
            header: <CustomTransComponent size={{ width: '100%' }} translationKey={'COMMON:NAME'} />,
            accessor: 'name',
            width: '12%',
            isDebounce: true,
            isFilterable: true,
            isSortable: true,
            cell: getName,
            skeleton: (
                <Stack direction={'row'} alignItems={'center'} gap={1}>
                    <Skeleton animation="wave" variant="circular">
                        <Avatar />
                    </Skeleton>
                    <Skeleton>
                        <Typography>
                            <CustomTransComponent translationKey={'DASHBOARD_MANAGEMENT:DASHBOARD_NAME'} />
                        </Typography>
                    </Skeleton>
                </Stack>
            ),
        },
        {
            header: <CustomTransComponent size={{ width: '100%' }} translationKey={'COMMON:ASSIGNMENT'} />,
            accessor: 'assignment',
            width: '12%',
            isFilterable: canViewUserDashboards && canViewSystemDashboards ? true : false,
            isDebounce: true,
            cell: (data: any): JSX.Element => getAssignment(data, 'assignment'),
            filterOptions: [
                { id: 'all', label: <CustomTransComponent translationKey={'SCHEDULING:ALL'} /> },
                { id: 'SYSTEM', label: <CustomTransComponent translationKey={'AUDIT_LOGS:SYSTEM'} /> },
                { id: 'USER', label: <CustomTransComponent translationKey={'AUDIT_LOGS:USER'} /> },
            ],
        },
        {
            header: <CustomTransComponent size={{ width: '100%' }} translationKey={'COMMON:LAST_MODIFICATION'} />,
            width: '24%',
            accessor: 'updatedAt',
            isFilterable: true,
            headerOptions: getDateHeaderOptions,
            typeVariant: 'body2',
            cell: getUpdateTime,
            headerSx: getDateSx,
            skeleton: <DateAndYearSkelton />,
        },
        {
            header: <CustomTransComponent size={{ width: '100%' }} translationKey={'COMMON:TYPE'} />,
            width: '12%',
            accessor: 'assignType',
            isFilterable: true,
            typeVariant: 'body2',
            filterOptions: [
                { id: 'DEVICE', label: <CustomTransComponent translationKey={'COMMON:SCHEDULING_DEVICE'} /> },
                { id: 'GROUP', label: <CustomTransComponent translationKey={'COMMON:SCHEDULING_GROUP'} /> },
                { id: 'SOURCE', label: <CustomTransComponent translationKey={'COMMON:SOURCE'} /> },
            ],
        },
        {
            header: <CustomTransComponent size={{ width: '100%' }} translationKey={'COMMON:DEFAULT'} />,
            isFilterable: true,
            accessor: 'status',
            width: '12%',
            cell: getActive,
            filterOptions: [
                { id: 'ACTIVE', label: <CustomTransComponent translationKey={'COMMON:ACTIVE'} /> },
                { id: 'INACTIVE', label: <CustomTransComponent translationKey={'COMMON:INACTIVE'} /> },
            ],
        },
        {
            header: <CustomTransComponent size={{ width: '100%' }} translationKey={'COMMON:USED_ON'} />,
            width: '7%',
            accessor: 'associatedCount',
        },
        {
            header: <CustomTransComponent size={{ width: '100%' }} translationKey={'COMMON:DESCRIPTION'} />,
            width: '14%',
            accessor: 'description',
            cell: getDescription,
        },
        {
            width: '8%',
            cell: getActionCell,
            skeleton: (
                <Stack direction={'row'} alignItems={'center'} gap={1.5}>
                    <Skeleton animation="wave" variant="circular">
                        <Avatar />
                    </Skeleton>
                    <Skeleton animation="wave" variant="circular">
                        <Avatar />
                    </Skeleton>
                </Stack>
            ),
        },
    ];

    const getModifiedData = (filters: any): any => {
        const modifiedFilters: any = JSON.parse(JSON.stringify(filters));
        if (modifiedFilters?.assignment?.length > 1) {
            modifiedFilters['assignment'] = 'ALL';
        } else if (modifiedFilters?.assignment?.length) {
            modifiedFilters['assignment'] = modifiedFilters?.assignment[0];
        }
        return modifiedFilters;
    };

    const [tableFilters] = useFilteredValues({ allFilters: headers, triggeredFilters: payload?.filters });

    const {
        data: dashboardsData,
        isLoading,
        isFetching,
    }: any = useGetAllDashboardsQuery(
        { body: { ...payload, filters: getModifiedData(tableFilters) } },
        { skip: !currentOrg?.id, refetchOnMountOrArgChange: true }
    );

    const getLabel = (key: string): string => {
        switch (key) {
            case 'assignType':
                return t('COMMON:TYPE');
            case 'updatedAt':
                return t('COMMON:LAST_MODIFICATION');
            default:
                return toFirstLetterUpperCase(key);
        }
    };

    return (
        <Stack p={3}>
            <Stack direction={'row'} spacing={2} justifyContent={'space-between'} alignItems={'center'}>
                <InputWithCustomPlaceholder
                    id="searchKey"
                    data-testid="dashboard-searchKey"
                    value={searchKey ?? ''}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>): void => {
                        setSearchKey(e.target.value);
                    }}
                    placeholder={t('COMMON:SEARCH_PLACEHOLDER')}
                />

                {payload?.filters && (
                    <Stack direction="row" spacing={1}>
                        {Object.keys(payload?.filters).map((key: string) => (
                            <Chip
                                key={`unique${key}`}
                                label={getLabel(key)}
                                onDelete={(): void => {
                                    handleChipDelete(key);
                                }}
                            />
                        ))}
                    </Stack>
                )}
            </Stack>
            <Paper className="margin-top-24">
                <CustomTable
                    ref={tableRef}
                    isPagination
                    handlePageChange={handleDashboardPageChange}
                    isLoading={isLoading || isFetching}
                    data={dashboardsData?.data?.records ?? []}
                    noDataFoundIcon={<Dashboard fontSize="inherit" />}
                    noDataFoundTitle={<CustomTransComponent translationKey={'DASHBOARDS:NO_DASHBOARD'} />}
                    keyToTraverse="id"
                    total={dashboardsData?.data?.total}
                    headers={headers}
                    handleFilterChange={handleFilterChange}
                    containerHeight={280}
                />
            </Paper>
            <DeleteDashboardModal data-testid="delete-dashboard-modal" ref={deleteRef} />
            <EditDashboardDetails data-testid="edit-dashboard-modal" ref={editRef} />
            <CreateDashboardModal
                data-testid="clone-dashboard-modal"
                actionButtonName={<CustomTransComponent translationKey={'DASHBOARDS:CLONE_DASHBOARD'} />}
                header={<CustomTransComponent translationKey={'DASHBOARDS:CLONE_DASHBOARD'} />}
                ref={cloneRef}
            />
            <AssociateDeviceModal data-testid="associate-device-modal" ref={associateDeviceRef} />
        </Stack>
    );
};
